import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import type { CreatePlanDTO, UpdatePlanDTO, BillingPeriod } from '../types';

// ─── Schema ──────────────────────────────────────────────────────────────────

const customEntrySchema = z.object({
  key: z.string().min(1),
  value: z.string(),
});

const planSchema = z.object({
  slug: z
    .string()
    .min(1)
    .regex(/^[a-z0-9-]+$/, 'Slug must be lowercase letters, numbers, and hyphens'),
  name_en: z.string().min(1),
  name_ar: z.string().min(1),
  billing_period: z.enum(['monthly', 'quarterly', 'yearly']),
  price_amount: z.coerce.number().min(0),
  currency: z.string().min(1).max(10),
  trial_days: z.coerce.number().int().min(0).optional(),
  is_active: z.boolean().optional(),
  product_id: z.string().optional(),
  // Features — quotas (empty string → null = unlimited)
  max_users: z.union([z.coerce.number().int().min(0), z.literal('')]).optional(),
  max_projects: z.union([z.coerce.number().int().min(0), z.literal('')]).optional(),
  max_storage_gb: z.union([z.coerce.number().min(0), z.literal('')]).optional(),
  max_api_calls_per_month: z.union([z.coerce.number().int().min(0), z.literal('')]).optional(),
  support_response_sla_hours: z.union([z.coerce.number().min(0), z.literal('')]).optional(),
  // Features — boolean flags
  has_advanced_analytics: z.boolean().optional(),
  has_priority_support: z.boolean().optional(),
  has_white_labeling: z.boolean().optional(),
  has_sso: z.boolean().optional(),
  has_audit_log_export: z.boolean().optional(),
  has_custom_domain: z.boolean().optional(),
  // Features — custom key-value
  custom_entries: z.array(customEntrySchema).optional(),
});

type PlanFormValues = z.infer<typeof planSchema>;

// ─── Types ────────────────────────────────────────────────────────────────────

interface PlanFormProps {
  mode: 'create' | 'edit';
  productId?: string;
  defaultValues?: Partial<PlanFormValues> & { features?: Record<string, unknown> };
  onSubmit: (data: CreatePlanDTO | UpdatePlanDTO) => Promise<void>;
  isSubmitting: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const QUOTA_FIELDS = [
  'max_users',
  'max_projects',
  'max_storage_gb',
  'max_api_calls_per_month',
] as const;

const BOOLEAN_FLAGS = [
  'has_sso',
  'has_advanced_analytics',
  'has_priority_support',
  'has_white_labeling',
  'has_audit_log_export',
  'has_custom_domain',
] as const;

type QuotaField = (typeof QUOTA_FIELDS)[number];
type BooleanFlag = (typeof BOOLEAN_FLAGS)[number];

function quotaToFormValue(v: unknown): number | '' {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  return isNaN(n) ? '' : n;
}

function formValueToQuota(v: number | '' | undefined): number | null | undefined {
  if (v === '' || v === undefined) return null;
  return v;
}

function buildFeaturesPayload(values: PlanFormValues): Record<string, unknown> {
  const features: Record<string, unknown> = {};

  for (const field of QUOTA_FIELDS) {
    const v = formValueToQuota(values[field] as number | '' | undefined);
    if (v !== undefined) features[field] = v;
  }

  for (const flag of BOOLEAN_FLAGS) {
    if (values[flag] !== undefined) features[flag] = values[flag];
  }

  const sla = formValueToQuota(values.support_response_sla_hours as number | '' | undefined);
  if (sla !== undefined) features['support_response_sla_hours'] = sla;

  if (values.custom_entries && values.custom_entries.length > 0) {
    const custom: Record<string, string | number | boolean | null> = {};
    for (const entry of values.custom_entries) {
      if (entry.key) {
        const num = Number(entry.value);
        if (entry.value === 'true') custom[entry.key] = true;
        else if (entry.value === 'false') custom[entry.key] = false;
        else if (!isNaN(num) && entry.value !== '') custom[entry.key] = num;
        else custom[entry.key] = entry.value;
      }
    }
    if (Object.keys(custom).length > 0) features['custom'] = custom;
  }

  return features;
}

function featuresDefaultValues(features?: Record<string, unknown>): Partial<PlanFormValues> {
  if (!features) return {};
  const custom = features['custom'] as Record<string, unknown> | undefined;
  return {
    max_users: quotaToFormValue(features['max_users']),
    max_projects: quotaToFormValue(features['max_projects']),
    max_storage_gb: quotaToFormValue(features['max_storage_gb']),
    max_api_calls_per_month: quotaToFormValue(features['max_api_calls_per_month']),
    support_response_sla_hours: quotaToFormValue(features['support_response_sla_hours']),
    has_advanced_analytics: features['has_advanced_analytics'] as boolean | undefined,
    has_priority_support: features['has_priority_support'] as boolean | undefined,
    has_white_labeling: features['has_white_labeling'] as boolean | undefined,
    has_sso: features['has_sso'] as boolean | undefined,
    has_audit_log_export: features['has_audit_log_export'] as boolean | undefined,
    has_custom_domain: features['has_custom_domain'] as boolean | undefined,
    custom_entries: custom
      ? Object.entries(custom).map(([key, value]) => ({ key, value: String(value ?? '') }))
      : [],
  };
}

// ─── Feature section subcomponents ───────────────────────────────────────────

function SectionHeader({ label }: { label: string }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mt-6 mb-3 border-b pb-1">
      {label}
    </p>
  );
}

// ─── Main form ────────────────────────────────────────────────────────────────

export function PlanForm({
  mode,
  productId,
  defaultValues,
  onSubmit,
  isSubmitting,
}: PlanFormProps) {
  const { t } = useTranslation();
  const isCreate = mode === 'create';

  const featureDefaults = featuresDefaultValues(defaultValues?.features);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<PlanFormValues>({
    resolver: zodResolver(planSchema),
    defaultValues: {
      slug: defaultValues?.slug ?? '',
      name_en: defaultValues?.name_en ?? '',
      name_ar: defaultValues?.name_ar ?? '',
      billing_period: (defaultValues?.billing_period as BillingPeriod) ?? 'monthly',
      price_amount: defaultValues?.price_amount ?? 0,
      currency: defaultValues?.currency ?? 'USD',
      trial_days: defaultValues?.trial_days ?? 0,
      is_active: defaultValues?.is_active ?? true,
      product_id: productId ?? defaultValues?.product_id ?? '',
      // Feature defaults
      max_users: featureDefaults.max_users ?? '',
      max_projects: featureDefaults.max_projects ?? '',
      max_storage_gb: featureDefaults.max_storage_gb ?? '',
      max_api_calls_per_month: featureDefaults.max_api_calls_per_month ?? '',
      support_response_sla_hours: featureDefaults.support_response_sla_hours ?? '',
      has_advanced_analytics: featureDefaults.has_advanced_analytics ?? false,
      has_priority_support: featureDefaults.has_priority_support ?? false,
      has_white_labeling: featureDefaults.has_white_labeling ?? false,
      has_sso: featureDefaults.has_sso ?? false,
      has_audit_log_export: featureDefaults.has_audit_log_export ?? false,
      has_custom_domain: featureDefaults.has_custom_domain ?? false,
      custom_entries: featureDefaults.custom_entries ?? [],
    },
  });

  const {
    fields: customFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'custom_entries',
  });

  const handleFormSubmit = handleSubmit(async (values) => {
    const features = buildFeaturesPayload(values);
    const common = {
      slug: values.slug,
      name_en: values.name_en,
      name_ar: values.name_ar,
      billing_period: values.billing_period,
      price_amount: values.price_amount,
      currency: values.currency,
      ...(values.trial_days !== undefined && { trial_days: values.trial_days }),
      ...(values.is_active !== undefined && { is_active: values.is_active }),
      features,
    };
    const payload = isCreate
      ? { product_id: productId ?? values.product_id ?? '', ...common }
      : common;

    await onSubmit(payload as CreatePlanDTO | UpdatePlanDTO);
  });

  const billingPeriods: BillingPeriod[] = ['monthly', 'quarterly', 'yearly'];

  return (
    <form onSubmit={handleFormSubmit} className="space-y-5">
      {/* ── Core fields ── */}
      <div className="space-y-1.5">
        <Label htmlFor="name_en">{t('plans.name')}</Label>
        <Input id="name_en" {...register('name_en')} aria-invalid={Boolean(errors.name_en)} />
        {errors.name_en && <p className="text-xs text-destructive">{errors.name_en.message}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="name_ar">{t('plans.nameAr')}</Label>
        <Input
          id="name_ar"
          dir="rtl"
          {...register('name_ar')}
          aria-invalid={Boolean(errors.name_ar)}
        />
        {errors.name_ar && <p className="text-xs text-destructive">{errors.name_ar.message}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="slug">{t('products.slug')}</Label>
        <Input id="slug" {...register('slug')} aria-invalid={Boolean(errors.slug)} />
        {errors.slug && <p className="text-xs text-destructive">{errors.slug.message}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="billing_period">{t('plans.billingPeriod')}</Label>
        <select
          id="billing_period"
          {...register('billing_period')}
          className="flex h-10 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {billingPeriods.map((period) => (
            <option key={period} value={period}>
              {t(`plans.periods.${period}`)}
            </option>
          ))}
        </select>
        {errors.billing_period && (
          <p className="text-xs text-destructive">{errors.billing_period.message}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="price_amount">{t('plans.price')}</Label>
          <Input
            id="price_amount"
            type="number"
            step="0.01"
            min="0"
            {...register('price_amount')}
            aria-invalid={Boolean(errors.price_amount)}
          />
          {errors.price_amount && (
            <p className="text-xs text-destructive">{errors.price_amount.message}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="currency">{t('plans.currency')}</Label>
          <Input id="currency" {...register('currency')} aria-invalid={Boolean(errors.currency)} />
          {errors.currency && <p className="text-xs text-destructive">{errors.currency.message}</p>}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="trial_days">{t('plans.trialDays')}</Label>
        <Input
          id="trial_days"
          type="number"
          min="0"
          {...register('trial_days')}
          aria-invalid={Boolean(errors.trial_days)}
        />
        {errors.trial_days && (
          <p className="text-xs text-destructive">{errors.trial_days.message}</p>
        )}
      </div>

      <div className="flex items-center gap-3">
        <Controller
          control={control}
          name="is_active"
          render={({ field }) => (
            <input
              id="is_active"
              type="checkbox"
              checked={field.value ?? false}
              onChange={(e) => field.onChange(e.target.checked)}
              className="h-4 w-4 rounded border-border text-primary focus:ring-ring"
            />
          )}
        />
        <Label htmlFor="is_active" className="cursor-pointer">
          {t('plans.isActive')}
        </Label>
      </div>

      {/* ── Features ── */}
      <div className="rounded-lg border border-border p-4 space-y-1">
        <p className="text-sm font-semibold">{t('plans.features.title')}</p>
        <p className="text-xs text-muted-foreground">{t('plans.features.unlimitedHint')}</p>

        {/* Quotas */}
        <SectionHeader label={t('plans.features.quotas')} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {QUOTA_FIELDS.map((field: QuotaField) => (
            <div key={field} className="space-y-1.5">
              <Label htmlFor={field}>{t(`plans.features.${field}`)}</Label>
              <Input
                id={field}
                type="number"
                min="0"
                placeholder={t('plans.features.unlimitedPlaceholder')}
                {...register(field as keyof PlanFormValues)}
              />
            </div>
          ))}
        </div>

        {/* Boolean flags */}
        <SectionHeader label={t('plans.features.flags')} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6">
          {BOOLEAN_FLAGS.map((flag: BooleanFlag) => (
            <div key={flag} className="flex items-center gap-2">
              <Controller
                control={control}
                name={flag}
                render={({ field }) => (
                  <input
                    id={flag}
                    type="checkbox"
                    checked={field.value ?? false}
                    onChange={(e) => field.onChange(e.target.checked)}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-ring"
                  />
                )}
              />
              <Label htmlFor={flag} className="cursor-pointer text-sm font-normal">
                {t(`plans.features.${flag}`)}
              </Label>
            </div>
          ))}
        </div>

        {/* Support */}
        <SectionHeader label={t('plans.features.support')} />
        <div className="space-y-1.5 max-w-xs">
          <Label htmlFor="support_response_sla_hours">
            {t('plans.features.support_response_sla_hours')}
          </Label>
          <Input
            id="support_response_sla_hours"
            type="number"
            min="0"
            placeholder={t('plans.features.unlimitedPlaceholder')}
            {...register('support_response_sla_hours')}
          />
        </div>

        {/* Custom key-value */}
        <SectionHeader label={t('plans.features.custom')} />
        <div className="space-y-2">
          {customFields.map((item, index) => (
            <div key={item.id} className="flex items-center gap-2">
              <Input
                placeholder={t('plans.features.customKey')}
                className="flex-1"
                {...register(`custom_entries.${index}.key`)}
              />
              <Input
                placeholder={t('plans.features.customValue')}
                className="flex-1"
                {...register(`custom_entries.${index}.value`)}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => remove(index)}
                className="shrink-0 text-muted-foreground hover:text-destructive"
              >
                ✕
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => append({ key: '', value: '' })}
          >
            + {t('plans.features.addCustom')}
          </Button>
        </div>
      </div>

      <Button type="submit" isLoading={isSubmitting} className="w-full">
        {isCreate ? t('plans.create') : t('plans.title')}
      </Button>
    </form>
  );
}
